import { InputError } from '@backstage/errors';
import { OrderNumberLookupResult } from '../../models/OrderNumberLookup';
import { ProductUsageCount } from '../../models/ProductUsageCount';
import { ProjectLink } from '../../models/ProjectLink';
import { ProvisioningOrder } from '../../models/ProvisioningOrder';
import { DataResult } from '../../utils/DataResult';
import {
  CancelToolOptions,
  CostCenterLookupOptions,
  OrderNumberLookupOptions,
  ProjectPayload,
  SaveProjectOptions,
  UpdateCustomFormDataOptions,
  UpdateProjectOwnershipOptions,
  UpdateProjectUserRolesOptions,
  UpdateServicePartnerCustomFormDataOptions,
  UpdateServicePartnerDataOptions,
  UpdateToolOptions,
} from '../api';
import { BaseApiService, toRequestError } from './http';
import {
  isProductUsageCount,
  isProjectLink,
  isProvisioningOrder,
  sanitizeProjectLink,
} from './shared';

function isProjectPayload(value: unknown): value is ProjectPayload {
  const project = value as { projectKey?: unknown } | null;

  return (
    typeof project === 'object' &&
    project !== null &&
    typeof project.projectKey === 'string'
  );
}

function isOrderNumberLookupResult(
  value: unknown,
): value is OrderNumberLookupResult {
  const lookup = value as OrderNumberLookupResult | null;

  return (
    typeof lookup === 'object' &&
    lookup !== null &&
    typeof lookup.coNumber === 'string'
  );
}

/**
 * Handles projects: CRUD, ownership/roles, tool management, provisioning
 * orders, links and the OU/cost-center lookups used by project forms.
 */
export class ProjectService extends BaseApiService {
  async getProject(projectKey: string): Promise<ProjectPayload | undefined> {
    const baseUrl = await this.getBaseUrl();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/project/${encodeURIComponent(projectKey)}`,
    );

    if (response.status === 404) {
      return undefined;
    }

    if (!response.ok) {
      throw await toRequestError(response);
    }

    const body: DataResult<unknown> | null = await response.json();

    return isProjectPayload(body?.data) ? body.data : undefined;
  }

  async saveProject({
    project,
    isNewProject,
  }: SaveProjectOptions): Promise<ProjectPayload | undefined> {
    const baseUrl = await this.getBaseUrl();

    const response = await this.fetchApi.fetch(`${baseUrl}/project`, {
      method: isNewProject ? 'POST' : 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(project),
    });

    if (!response.ok) {
      throw await toRequestError(response);
    }

    const body: DataResult<unknown> | null = await response.json();

    return isProjectPayload(body?.data) ? body.data : undefined;
  }

  async getProjectProvisioningOrders(
    projectKey: string,
  ): Promise<ProvisioningOrder[]> {
    return this.fetchArray(
      `/project/${encodeURIComponent(projectKey)}/orders`,
      isProvisioningOrder,
    );
  }

  async getToolProvisioningOrders(
    projectKey: string,
    toolId: string,
  ): Promise<ProvisioningOrder[]> {
    return this.fetchArray(
      `/project/${encodeURIComponent(projectKey)}/tools/${encodeURIComponent(
        toolId,
      )}/orders`,
      isProvisioningOrder,
    );
  }

  async updateProjectOwnership({
    projectKey,
    ownerUserId,
    coOwnerUserId,
  }: UpdateProjectOwnershipOptions): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const query = new URLSearchParams({ projectKey });

    if (ownerUserId) {
      query.set('ownerUserId', ownerUserId);
    }

    if (coOwnerUserId) {
      query.set('coOwnerUserId', coOwnerUserId);
    }

    const response = await this.fetchApi.fetch(
      `${baseUrl}/project/users/updateOwnership?${query.toString()}`,
      { method: 'PUT' },
    );

    if (!response.ok) {
      throw await toRequestError(response);
    }
  }

  async updateProjectUserRoles({
    projectKey,
    roles,
  }: UpdateProjectUserRolesOptions): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const query = new URLSearchParams({ projectKey });

    const response = await this.fetchApi.fetch(
      `${baseUrl}/project/users/update?${query.toString()}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roles }),
      },
    );

    if (!response.ok) {
      throw await toRequestError(response);
    }
  }

  async updateServicePartnerData({
    projectKey,
    servicePartnerData,
  }: UpdateServicePartnerDataOptions): Promise<boolean> {
    const baseUrl = await this.getBaseUrl();
    const query = new URLSearchParams({ projectKey });

    const response = await this.fetchApi.fetch(
      `${baseUrl}/project/servicePartnerData/update?${query.toString()}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(servicePartnerData),
      },
    );

    if (!response.ok) {
      throw await toRequestError(response);
    }

    const body: DataResult<unknown> | null = await response.json();

    return Boolean(body?.data);
  }

  async updateProjectDataSelfAssessment(
    projectKey: string,
    data: unknown,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const query = new URLSearchParams({ projectKey });

    await this.put(
      `${baseUrl}/project/dataSelfAssessment?${query.toString()}`,
      data,
    );
  }

  async getProjectKeySuggestion(
    projectName: string,
  ): Promise<string | undefined> {
    const baseUrl = await this.getBaseUrl();
    const query = new URLSearchParams({ name: projectName });

    const response = await this.fetchApi.fetch(
      `${baseUrl}/project/projectKey?${query.toString()}`,
    );

    if (!response.ok) {
      throw await toRequestError(response);
    }

    const body: DataResult<{ result?: unknown }> | null = await response.json();
    const projectKey = body?.data?.result;

    return typeof projectKey === 'string' && projectKey.length > 0
      ? projectKey
      : undefined;
  }

  async updateToolCustomFormData({
    customFormId,
    data,
    instanceId,
    projectKey,
  }: UpdateCustomFormDataOptions): Promise<void> {
    if (!projectKey) {
      throw new InputError(
        'A project key is required to update the custom form data of a tool',
      );
    }

    const baseUrl = await this.getBaseUrl();
    const query = new URLSearchParams({
      projectKey,
      toolOrChildInstanceId: instanceId,
    });

    await this.put(`${baseUrl}/project/tool/formdata?${query.toString()}`, {
      id: customFormId,
      data,
    });
  }

  async updateServicePartnerCustomFormData({
    projectKey,
    name,
    id,
    data,
  }: UpdateServicePartnerCustomFormDataOptions): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const query = new URLSearchParams({ projectKey });

    await this.put(
      `${baseUrl}/project/servicePartnerData/update?${query.toString()}`,
      { name, id, data },
    );
  }

  async cancelTool({
    projectKey,
    toolId,
    force,
  }: CancelToolOptions): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const query = new URLSearchParams();

    if (force !== undefined) {
      query.set('force', String(force));
    }

    const search = query.toString();

    await this.put(
      `${baseUrl}/project/${encodeURIComponent(
        projectKey,
      )}/tools/${encodeURIComponent(toolId)}/cancel${
        search ? `?${search}` : ''
      }`,
    );
  }

  async undoCancelTool(projectKey: string, toolId: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    await this.put(
      `${baseUrl}/project/${encodeURIComponent(
        projectKey,
      )}/tools/${encodeURIComponent(toolId)}/cancel/undo`,
    );
  }

  async getProjectToolLinks(projectKey: string): Promise<ProjectLink[]> {
    const query = new URLSearchParams({ projectKey });
    const links = await this.fetchArray(
      `/project/links?${query.toString()}`,
      isProjectLink,
    );

    return links.map(sanitizeProjectLink);
  }

  async updateTool({
    projectKey,
    tool,
    validateOnly,
  }: UpdateToolOptions): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const query = new URLSearchParams({ projectKey });

    if (validateOnly) {
      query.set('validateOnly', 'true');
    }

    await this.put(`${baseUrl}/project/tool/update?${query.toString()}`, tool);
  }

  async getToolUsageCounts(): Promise<ProductUsageCount[]> {
    return this.fetchArray('/project/tools/counts', isProductUsageCount);
  }

  async getOrderNumberLookup({
    ou,
    internalOrderNumber,
    signal,
  }: OrderNumberLookupOptions): Promise<OrderNumberLookupResult | undefined> {
    const baseUrl = await this.getBaseUrl();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/project/colookup/${encodeURIComponent(
        ou,
      )}/${encodeURIComponent(internalOrderNumber)}`,
      { signal },
    );

    if (!response.ok) {
      throw await toRequestError(response);
    }

    const body: DataResult<unknown> | null = await response.json();

    return isOrderNumberLookupResult(body?.data) ? body.data : undefined;
  }

  async getOrderNumberCount(ou: string, signal?: AbortSignal): Promise<number> {
    const baseUrl = await this.getBaseUrl();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/project/cocountlookup/${encodeURIComponent(ou)}`,
      { signal },
    );

    if (!response.ok) {
      throw await toRequestError(response);
    }

    const body: DataResult<unknown> | null = await response.json();
    const count = body?.data;

    return typeof count === 'number' && Number.isFinite(count) ? count : 0;
  }

  async getCostCenterLookup(
    options: CostCenterLookupOptions,
  ): Promise<string[]> {
    return this.lookupCostCenters('costcenterlookup', options);
  }

  async getCompleteCostCenterLookup(
    options: CostCenterLookupOptions,
  ): Promise<string[]> {
    return this.lookupCostCenters('completecolookup', options);
  }

  private async lookupCostCenters(
    path: string,
    { ou, searchTerm, signal }: CostCenterLookupOptions,
  ): Promise<string[]> {
    return this.fetchArray(
      `/project/${path}/${encodeURIComponent(ou)}/${encodeURIComponent(
        searchTerm,
      )}`,
      (costCenter): costCenter is string => typeof costCenter === 'string',
      { signal },
    );
  }
}
